/* =====================================================================
   18 KARATS — redesign.js  (CipherForces, 2026-09-22, rev 2026-10-12)
   Loaded site-wide with `defer`. Each part does nothing on pages where
   its elements don't exist.

   1) Custom dropdowns instead of the browser's native lists — collection
      "Sort" and product option dropdowns (e.g. Ring Size). The arrow flips
      up when open; keyboard (arrows, type-to-jump) + screen-reader friendly.
      The native <select> stays in the page and keeps doing the work, so the
      theme's sorting and the options app's cart properties are unchanged.
   2) Gallery title ("18 Karats | Jewelry") — the next word slides in as the
      old one leaves (no blank gap), and the phrase stays centred: the word
      box sizes to the current word instead of a fixed 300px.
   3) Gallery image tiles — a picture is never shown in two tiles at once,
      and a third picture no longer flashes during a tile's cross-fade.
   4) Mobile menu: "By Shape" and "By Style" always open, icons and names
      in a 2-column grid (tickets A20/A21, 2026-09-25).
   5) Diamond app (Nivoda): US spelling "Color" (ticket A6, 2026-09-25).
   6) Product page dropdowns (Description / Product Details): the arrow
      and title turn back as soon as a panel starts closing (T-1003).
   ===================================================================== */
(function () {
  'use strict';

  function debounce(fn, ms) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  /* ------------------------------------------------------------------
     1) CUSTOM DROPDOWNS  (collection Sort + product option dropdowns)
     One component for every dropdown, named .cf-sort after its first use.
     The native <select> always stays in the page and keeps doing the work
     (the theme's sorting, the options app's cart properties / validation);
     this only replaces what the visitor sees and clicks.
       sort  — borderless "Sort ⌄" trigger (theme <sort-by>)
       field — boxed form field showing the chosen value (e.g. Ring Size)
     ------------------------------------------------------------------ */
  var ddUid = 0;
  var CHEVRON = '<svg class="cf-sort__chev" viewBox="0 0 12 8" aria-hidden="true" focusable="false"><path d="M1 1.5l5 5 5-5"/></svg>';
  var CHECK = '<svg class="cf-sort__check" viewBox="0 0 12 10" aria-hidden="true" focusable="false"><path d="M1 5.2l3.4 3.3L11 1.5"/></svg>';
  var LIST_MAX = 320; /* px — matches the CSS max-height of field lists */

  function closeAllDropdowns(except) {
    var open = document.querySelectorAll('.cf-sort.is-open');
    for (var i = 0; i < open.length; i++) {
      if (open[i] !== except && open[i]._cfClose) open[i]._cfClose(false);
    }
  }

  // A disabled option with an empty value ("Choose one") is a prompt, not a choice.
  function isPrompt(opt) { return opt.disabled && opt.value === ''; }
  // Options the app marks as an unavailable combination (still selectable, shown muted).
  function isUnavailable(opt) { return /\bbcpo-(disabled|unavailable)\b/.test(opt.className); }
  // Set a select's value in a way that frameworks tracking the value (React) also notice.
  var nativeSelectValue = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');
  function setSelectValue(select, value) {
    if (nativeSelectValue && nativeSelectValue.set) nativeSelectValue.set.call(select, value);
    else select.value = value;
  }
  function textOf(el) { return el ? el.textContent.replace(/[\s*:]+$/, '').trim() : ''; }
  // Bottom edge of a sticky/fixed bar at the top of the screen (the site header),
  // so a list opening upwards never slides underneath it.
  function topInset() {
    var els = document.elementsFromPoint ? document.elementsFromPoint(window.innerWidth / 2, 2) : [];
    var bottom = 0;
    for (var i = 0; i < els.length; i++) {
      for (var e = els[i]; e && e !== document.body && e !== document.documentElement; e = e.parentElement) {
        var pos = getComputedStyle(e).position;
        if (pos === 'fixed' || pos === 'sticky') {
          var r = e.getBoundingClientRect();
          if (r.top <= 2 && r.bottom > bottom && r.bottom < window.innerHeight / 2) bottom = r.bottom;
          break;
        }
      }
    }
    return bottom;
  }
  function debounceMax(fn, wait, max) {
    var t, first = 0;
    return function () {
      var now = Date.now();
      if (!first) first = now;
      clearTimeout(t);
      var run = function () { first = 0; fn(); };
      if (now - first >= max) run(); else t = setTimeout(run, wait);
    };
  }

  function buildDropdown(select, cfg) {
    select.setAttribute('data-cf-enhanced', '');
    var id = 'cf-dd-' + (++ddUid);
    var wrap = document.createElement('div');
    wrap.className = 'cf-sort cf-sort--' + cfg.mode;
    wrap._cfSelect = select;
    select._cfWrap = wrap;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.id = id + '-btn';
    btn.className = 'cf-sort__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', id);
    btn.innerHTML = '<span class="cf-sort__value"></span>' + CHEVRON;
    var valueEl = btn.querySelector('.cf-sort__value');
    // Screen-reader name = the field's own label + the current value ("Ring Size (Inches): 7.0").
    if (cfg.labelIds) btn.setAttribute('aria-labelledby', cfg.labelIds + ' ' + btn.id);
    var baseDescribedBy = select.getAttribute('aria-describedby') || '';
    // "required" is announced from a visually hidden note (a button can't be required itself)
    var reqNote = document.createElement('span');
    reqNote.className = 'cf-sort__sr';
    reqNote.id = id + '-req';
    reqNote.textContent = 'required';

    var list = document.createElement('ul');
    list.className = 'cf-sort__list';
    list.id = id;
    list.setAttribute('role', 'listbox');
    list.setAttribute('tabindex', '-1');
    list.setAttribute('aria-label', cfg.listLabel);

    wrap.appendChild(btn);
    wrap.appendChild(list);
    wrap.appendChild(reqNote);
    cfg.insertAfter.parentNode.insertBefore(wrap, cfg.insertAfter.nextSibling);

    var signature = '';
    function items() { return Array.prototype.slice.call(list.querySelectorAll('.cf-sort__opt')); }
    function choosable() { return items().filter(function (li) { return li.getAttribute('aria-disabled') !== 'true'; }); }
    function isOpen() { return wrap.classList.contains('is-open'); }

    // Mirror the native select (options, disabled, selection, invalid) into the list.
    function sync() {
      var sig = Array.prototype.map.call(select.options, function (o) {
        return o.value + '\u0001' + o.text + '\u0001' + (o.disabled ? 1 : 0) + '\u0001' + (o.hidden ? 1 : 0) + '\u0001' + (isUnavailable(o) ? 1 : 0);
      }).join('\u0002');
      if (sig !== signature) {
        signature = sig;
        var focusedValue = list.contains(document.activeElement) && document.activeElement.getAttribute ? document.activeElement.getAttribute('data-value') : null;
        list.innerHTML = '';
        Array.prototype.forEach.call(select.options, function (opt, i) {
          if (isPrompt(opt) || opt.hidden) return;
          var li = document.createElement('li');
          li.className = 'cf-sort__opt';
          li.id = id + '-o' + i;
          li.setAttribute('role', 'option');
          li.setAttribute('tabindex', '-1');
          li.setAttribute('data-value', opt.value);
          if (opt.disabled) { li.setAttribute('aria-disabled', 'true'); li.classList.add('is-disabled'); }
          else if (isUnavailable(opt)) li.classList.add('is-unavailable');
          li.innerHTML = '<span class="cf-sort__text"></span>' + CHECK;
          li.querySelector('.cf-sort__text').textContent = opt.text.trim();
          list.appendChild(li);
        });
        if (focusedValue !== null && isOpen()) {
          var again = choosable().filter(function (li) { return li.getAttribute('data-value') === focusedValue; })[0];
          if (again) focusItem(again); else btn.focus({ preventScroll: true });
        }
      }
      var chosen = select.selectedIndex > -1 ? select.options[select.selectedIndex] : null;
      var hasValue = !!chosen && !isPrompt(chosen);
      items().forEach(function (li) {
        var on = hasValue && li.getAttribute('data-value') === select.value;
        li.classList.toggle('is-selected', on);
        li.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (cfg.mode === 'field') {
        var prompt = Array.prototype.filter.call(select.options, isPrompt)[0];
        valueEl.textContent = hasValue ? chosen.text.trim() : (prompt ? prompt.text.trim() : 'Select');
        valueEl.classList.toggle('is-placeholder', !hasValue);
        if (select.getAttribute('aria-invalid') === 'true') setInvalid(true);
        else if (hasValue) setInvalid(false);
      } else {
        valueEl.textContent = cfg.fixedLabel;
      }
      if (!cfg.labelIds) {
        var name = cfg.listLabel;
        if (cfg.mode === 'sort') name = cfg.fixedLabel === name ? name : name + ': ' + cfg.fixedLabel;
        else name = name + ': ' + valueEl.textContent;
        btn.setAttribute('aria-label', name);
      }
      btn.disabled = select.disabled;
      if (select.disabled) close(false);
      describe();
    }

    // Link the options app's "please choose" message to the field while it shows.
    var appMsgId = '';
    function setInvalid(on) {
      wrap.classList.toggle('is-invalid', on);
      if (on) btn.setAttribute('aria-invalid', 'true'); else btn.removeAttribute('aria-invalid');
      var msg = on && select.closest('#bcpo') ? ((select.form || document).querySelector('.bcpo-validation') || document.querySelector('.bcpo-validation')) : null;
      if (msg) {
        if (!msg.id) msg.id = 'cf-bcpo-msg-' + (++ddUid);
        appMsgId = msg.id;
      } else if (!on) {
        appMsgId = '';
      }
      describe();
    }
    function describe() {
      var ids = [baseDescribedBy, select.required ? reqNote.id : '', appMsgId].join(' ').replace(/\s+/g, ' ').trim();
      if (ids) btn.setAttribute('aria-describedby', ids); else btn.removeAttribute('aria-describedby');
    }
    // Focus an option without scrolling the page; scroll only inside the list.
    function focusItem(li) {
      li.focus({ preventScroll: true });
      var top = li.offsetTop, bottom = top + li.offsetHeight;
      if (top < list.scrollTop) list.scrollTop = top - 6;
      else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 6;
    }
    wrap._cfSync = sync;
    wrap._cfInvalid = setInvalid;

    function open(focusWhich) {
      if (cfg.mode === 'sort') resetToDefault(select); /* the page's real sort, not a browser-restored one */
      sync();
      if (select.disabled || !choosable().length) return;
      closeAllDropdowns(wrap);
      // Open toward the side with room (never under the sticky header); if neither
      // side fits, cap the height to the larger one.
      wrap.classList.remove('cf-sort--up');
      list.style.maxHeight = '';
      list.style.left = list.style.right = '';
      var r = btn.getBoundingClientRect();
      var natural = Math.min(list.scrollHeight, cfg.mode === 'field' ? LIST_MAX : 100000);
      var below = window.innerHeight - r.bottom - 12;
      var above = r.top - 12 - topInset();
      if (natural > below && above > below) wrap.classList.add('cf-sort--up');
      var room = Math.max(below, above, 120);
      if (natural > room) { list.style.maxHeight = room + 'px'; list.style.overflowY = 'auto'; }
      wrap.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      wrap._cfOpenY = window.scrollY;
      wrap._cfOpenW = window.innerWidth;
      // keep the list on screen sideways (e.g. a small trigger at the right edge)
      var lr = list.getBoundingClientRect();
      if (lr.right > document.documentElement.clientWidth - 8) { list.style.left = 'auto'; list.style.right = '0'; }
      else if (lr.left < 8) { list.style.left = '0'; list.style.right = 'auto'; }
      var all = choosable();
      var s = list.querySelector('.is-selected');
      if (s) list.scrollTop = s.offsetTop - (list.clientHeight - s.offsetHeight) / 2;
      var target = focusWhich === 'first' ? all[0] : focusWhich === 'last' ? all[all.length - 1] : focusWhich ? (s || all[0]) : null;
      if (target) focusItem(target);
    }
    function close(returnFocus) {
      if (!isOpen()) return;
      wrap.classList.remove('is-open');
      list.style.maxHeight = '';
      btn.setAttribute('aria-expanded', 'false');
      if (returnFocus) btn.focus();
    }
    wrap._cfClose = close;

    function choose(li) {
      if (select.disabled || li.getAttribute('aria-disabled') === 'true') return;
      var value = li.getAttribute('data-value');
      close(true);
      if (value === select.value && select.selectedIndex > -1 && !isPrompt(select.options[select.selectedIndex])) return;
      setSelectValue(select, value);
      sync();
      if (cfg.onChoose && cfg.onChoose(value) === false) return;
      // real bubbling events — the options app listens for 'change' on the option's wrapper
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      setTimeout(sync, 0);
      setTimeout(sync, 60); /* the app adjusts related options in timeouts */
    }

    // Type-ahead: "7" → 7.0, "12" → 12.0, "5.2" → 5.25, "pr" → Price…; the same
    // letter again moves to the next match ("7","7" → 7.25) when nothing starts with "77".
    var typed = '', typedAt = 0;
    function typeAhead(ch) {
      var now = Date.now();
      typed = (now - typedAt > 700 ? '' : typed) + ch.toLowerCase();
      typedAt = now;
      var all = choosable();
      if (!all.length) return;
      function starts(term) { return all.filter(function (li) { return li.textContent.trim().toLowerCase().indexOf(term) === 0; }); }
      var hit = null;
      if (/^(.)\1*$/.test(typed)) {
        // one character (maybe repeated): "11" still finds 11.0 first; otherwise step
        // to the next option starting with it, after the current one
        if (typed.length > 1) hit = starts(typed)[0];
        if (!hit) {
          var hits = starts(typed.charAt(0));
          var ref = list.contains(document.activeElement) ? document.activeElement : list.querySelector('.is-selected');
          hit = hits.length ? hits[(hits.indexOf(ref) + 1) % hits.length] : null;
        }
      } else {
        hit = starts(typed)[0];
      }
      if (!hit) return;
      if (!isOpen()) open(false);
      focusItem(hit);
    }

    btn.addEventListener('click', function () {
      if (isOpen()) { close(false); return; }
      if (document.activeElement !== btn) btn.focus({ preventScroll: true });
      // something else (e.g. a newsletter popup's focus trap) is holding focus — stay closed
      if (document.activeElement !== btn && document.activeElement !== document.body) return;
      open(false);
    });
    btn.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!isOpen()) open(true); else { var s = list.querySelector('.is-selected') || choosable()[0]; if (s) focusItem(s); }
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        open(e.key === 'Home' ? 'first' : 'last');
      } else if (e.key === 'Escape') {
        close(false);
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        typeAhead(e.key);
      }
    });
    list.addEventListener('click', function (e) {
      var li = e.target.closest('.cf-sort__opt');
      if (li) choose(li);
    });
    list.addEventListener('keydown', function (e) {
      var all = choosable();
      if (!all.length) { if (e.key === 'Escape') { e.preventDefault(); close(true); } else if (e.key === 'Tab') close(false); return; }
      var i = all.indexOf(document.activeElement);
      var last = all.length - 1;
      function go(n) { e.preventDefault(); focusItem(all[Math.max(0, Math.min(last, n))]); }
      if (e.key === 'ArrowDown') go(i < 0 ? 0 : i + 1);
      else if (e.key === 'ArrowUp') go(i < 0 ? 0 : i - 1);
      else if (e.key === 'PageDown') go(i + 8);
      else if (e.key === 'PageUp') go(i < 0 ? 0 : i - 8);
      else if (e.key === 'Home') go(0);
      else if (e.key === 'End') go(last);
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (i > -1) choose(all[i]); }
      else if (e.key === 'Escape') { e.preventDefault(); close(true); }
      else if (e.key === 'Tab') { close(false); }
      else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) { typeAhead(e.key); }
    });
    // Close when focus moves somewhere else (Tab away, a popup taking focus…).
    wrap.addEventListener('focusout', function (e) {
      if (!isOpen() || (e.relatedTarget && wrap.contains(e.relatedTarget))) return;
      setTimeout(function () {
        if (isOpen() && document.activeElement && document.activeElement !== document.body && !wrap.contains(document.activeElement)) close(false);
      }, 0);
    });

    // Keep in step when other code changes the select (app resets, variant switches).
    select.addEventListener('change', sync);
    select.addEventListener('invalid', function () { setInvalid(true); });
    // Code that focuses the hidden select (e.g. the options app pointing at a
    // missing required choice) lands on the visible dropdown instead.
    select.addEventListener('focus', function () {
      if (select.validationMessage) setInvalid(true);
      btn.focus();
    });
    if (select.form) select.form.addEventListener('reset', function () { setTimeout(sync, 0); });
    if (window.MutationObserver) {
      new MutationObserver(debounce(sync, 30)).observe(select, { childList: true, subtree: true, attributes: true, attributeFilter: ['selected', 'disabled', 'hidden', 'label', 'value', 'class', 'aria-invalid'] });
    }
    sync();
    return wrap;
  }

  // Remove a custom dropdown whose select is gone or was re-rendered by other code.
  function dropStale(container) {
    var ws = container.querySelectorAll('.cf-sort');
    for (var i = 0; i < ws.length; i++) {
      var s = ws[i]._cfSelect;
      if (!s || !s.isConnected || s._cfWrap !== ws[i]) ws[i].parentNode.removeChild(ws[i]);
    }
  }
  // True when this select already has a live custom dropdown. A select whose page
  // HTML was rebuilt by other code keeps our marker but lost its dropdown — reset it.
  function alreadyDone(select) {
    if (!select.hasAttribute('data-cf-enhanced')) return false;
    if (select._cfWrap && select._cfWrap.isConnected) return true;
    select.removeAttribute('data-cf-enhanced');
    select.classList.remove('cf-sort-native-field');
    select.removeAttribute('aria-hidden');
    select.removeAttribute('tabindex');
    return false;
  }

  // Collection "Sort" (theme <sort-by>).
  function enhanceSort(sortBy) {
    var select = sortBy.querySelector('select');
    if (!select || alreadyDone(select) || !select.options.length) return;
    dropStale(sortBy);
    // Start from the sort the page was actually rendered with (the browser can
    // restore an older choice into the select when going Back).
    resetToDefault(select);
    var nativeWrap = select.closest('.element-select__wrapper') || select.parentElement;
    var themeLabel = nativeWrap.querySelector('.element-button .element-text');
    buildDropdown(select, {
      mode: 'sort',
      insertAfter: nativeWrap,
      listLabel: 'Sort',
      fixedLabel: (themeLabel && themeLabel.textContent.trim()) || 'Sort',
      onChoose: function (value) {
        // If the theme's sort component hasn't hydrated yet, navigate directly —
        // this is the same URL the theme produces after sorting.
        if (sortBy.hasAttribute('defer-hydration') || !window.customElements || !customElements.get('sort-by')) {
          var url = new URL(window.location.href);
          url.searchParams.set('sort_by', value);
          url.searchParams.delete('page');
          window.location.href = url.toString();
          return false;
        }
      }
    });
    nativeWrap.classList.add('cf-sort-native'); /* hidden via CSS; stays in the DOM for the theme */
  }
  function resetToDefault(select) {
    var def = Array.prototype.filter.call(select.options, function (o) { return o.defaultSelected; })[0];
    if (def && select.value !== def.value) setSelectValue(select, def.value);
  }
  // Coming Back to a page kept in the browser's memory: the select still holds the
  // choice made just before leaving, but the page shows the original sort.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    var ss = document.querySelectorAll('sort-by select[data-cf-enhanced]');
    for (var i = 0; i < ss.length; i++) {
      resetToDefault(ss[i]);
      if (ss[i]._cfWrap && ss[i]._cfWrap._cfSync) { ss[i]._cfWrap._cfClose(false); ss[i]._cfWrap._cfSync(); }
    }
  });

  // Form dropdowns. The select stays exactly where it is (visually hidden), so the
  // app's own code, required-field checks and error messages keep working.
  var FIELD_SELECTS = [
    // product options app (Ring Size, Backing, option dropdowns) — never its hidden master variant select
    { sel: '#bcpo .selector-wrapper select:not(#bcpo-select):not([name="vopo-id"]):not([name="id"])' },
    // Judge.me reviews "Sort" on product pages
    { sel: 'select.jdgm-sort-dropdown', label: 'Sort reviews', mod: 'compact' }
    // (The Custom Ring Order form is left as it is — its fields use the form app's
    //  own underline style and it sits behind a customer login.)
  ];
  // id(s) of the element(s) naming this select: aria-labelledby, a <label for>,
  // or the options app's title next to it.
  function labelIdsFor(select) {
    var ids = (select.getAttribute('aria-labelledby') || '').split(/\s+/).filter(function (x) { return x && document.getElementById(x); });
    if (ids.length) return ids.join(' ');
    var el = select.labels && select.labels[0];
    if (!el) { var w = select.closest('.selector-wrapper'); el = w && w.querySelector('.bcpo-title'); }
    if (!el) return '';
    if (!el.id) el.id = 'cf-lbl-' + (++ddUid);
    return el.id;
  }
  function enhanceField(select, def) {
    if (alreadyDone(select) || select.multiple || select.size > 1 || !select.options.length) return;
    if (select.closest('.bcpo-grid')) return;
    var host = select.parentElement;
    dropStale(host);
    var labelIds = labelIdsFor(select);
    var firstLabel = labelIds && document.getElementById(labelIds.split(' ')[0]);
    var labelText = textOf(firstLabel) || def.label || select.getAttribute('aria-label') || 'Options';
    host.classList.add('cf-sort-host');
    select.classList.add('cf-sort-native-field');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');
    var wrap = buildDropdown(select, { mode: 'field', insertAfter: select, listLabel: labelText, labelIds: labelIds });
    if (def.mod) wrap.classList.add('cf-sort--' + def.mod);
  }

  // The options app shows its own "please choose" message for an empty required
  // option; on iPhone/iPad/Mac it doesn't focus the field, so watch the message.
  function watchAppValidation() {
    var vs = document.querySelectorAll('.bcpo-validation');
    for (var i = 0; i < vs.length; i++) {
      var v = vs[i];
      if (v._cfWatched || !window.MutationObserver) continue;
      v._cfWatched = true;
      new MutationObserver(function (muts) {
        var el = muts[0].target;
        var bad = window.bcpo && window.bcpo.invalidEl;
        var ui = bad && bad._cfWrap;
        if (getComputedStyle(el).display !== 'none' && ui && ui._cfInvalid) ui._cfInvalid(true);
      }).observe(v, { attributes: true, attributeFilter: ['style', 'class'] });
    }
  }

  function initDropdowns() {
    var sorts = document.querySelectorAll('sort-by');
    for (var i = 0; i < sorts.length; i++) enhanceSort(sorts[i]);
    FIELD_SELECTS.forEach(function (def) {
      var fields = document.querySelectorAll(def.sel);
      for (var j = 0; j < fields.length; j++) enhanceField(fields[j], def);
    });
    watchAppValidation();
  }

  document.addEventListener('pointerdown', function (e) {
    if (!e.target.closest || !e.target.closest('.cf-sort')) closeAllDropdowns(null);
  });
  document.addEventListener('focusin', function (e) {
    var open = document.querySelectorAll('.cf-sort.is-open');
    for (var i = 0; i < open.length; i++) if (!open[i].contains(e.target)) open[i]._cfClose(false);
  });
  // A native dropdown closes when the page scrolls or the phone rotates — so do these.
  window.addEventListener('scroll', function () {
    var open = document.querySelectorAll('.cf-sort.is-open');
    for (var i = 0; i < open.length; i++) if (Math.abs(window.scrollY - (open[i]._cfOpenY || 0)) > 40) open[i]._cfClose(false);
  }, { passive: true });
  window.addEventListener('resize', function () {
    var open = document.querySelectorAll('.cf-sort.is-open');
    for (var i = 0; i < open.length; i++) if (window.innerWidth !== open[i]._cfOpenW) open[i]._cfClose(false);
  });
  // The browser may put an older sort choice back into the select after load.
  window.addEventListener('load', function () {
    var ss = document.querySelectorAll('sort-by select[data-cf-enhanced]');
    for (var i = 0; i < ss.length; i++) { resetToDefault(ss[i]); if (ss[i]._cfWrap) ss[i]._cfWrap._cfSync(); }
  });
  // The options app sometimes changes one option's select silently when another
  // option changes (variant switches) — re-read every dropdown in that block.
  document.addEventListener('change', function (e) {
    var root = e.target && e.target.closest && e.target.closest('#bcpo');
    if (!root) return;
    var resync = function () {
      var ws = root.querySelectorAll('.cf-sort');
      for (var i = 0; i < ws.length; i++) if (ws[i]._cfSync) ws[i]._cfSync();
    };
    setTimeout(resync, 0);
    setTimeout(resync, 60);
  }, true);

  /* ------------------------------------------------------------------
     2) GALLERY — ROTATING TITLE
     ------------------------------------------------------------------ */
  function fixRotatingTitle(el) {
    if (el._cfFixed || typeof el.rotateWord !== 'function') return;
    var box = el.querySelector('[class^="ai-rotating-title__rotating-"]');
    var words = Array.prototype.slice.call(el.querySelectorAll('[class^="ai-rotating-title__word-"]'));
    if (!box || words.length < 2) return;
    el._cfFixed = true;

    var widths = [];
    function current() {
      var i = el.currentIndex;
      return (typeof i === 'number' && words[i]) ? i : 0;
    }
    function sync() {
      widths = words.map(function (w) { return Math.ceil(w.getBoundingClientRect().width); });
      if (widths[current()]) box.style.width = widths[current()] + 'px';
    }

    sync();
    el.classList.add('cf-rt-ready'); /* CSS drops the fixed 300px min-width only once the box is sized */

    // No blank gap: the next word comes in as the current one leaves (the
    // slide/fade itself is in redesign.css).
    el.rotateWord = function () {
      var prev = words[current()];
      el.currentIndex = (current() + 1) % words.length;
      var next = words[el.currentIndex];
      if (widths[el.currentIndex]) box.style.width = widths[el.currentIndex] + 'px';
      prev.classList.remove('active');
      prev.classList.add('exit');
      next.classList.remove('exit');
      next.classList.add('active');
      setTimeout(function () { prev.classList.remove('exit'); }, 650);
    };

    window.addEventListener('resize', debounce(sync, 150));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(sync);
  }

  /* ------------------------------------------------------------------
     3) GALLERY — IMAGE TILES (no duplicates)
     ------------------------------------------------------------------ */
  function fixGallery(el) {
    if (el._cfFixed || typeof el.rotateCell !== 'function' || !Array.isArray(el.allImages) || !el.allImages.length) return;
    el._cfFixed = true;

    function cells() { return Array.prototype.slice.call(el.querySelectorAll('[data-cell-index]')); }
    function shown(cell) { return cell.offsetParent !== null; }
    function slot(cell, name) { return cell.querySelector('img[data-slot="' + name + '"]'); }
    function keysIn(name, exceptCell) {
      var s = new Set();
      cells().forEach(function (c) {
        if (c === exceptCell || !shown(c)) return;
        var im = slot(c, name);
        if (im && im.dataset.imageKey) s.add(im.dataset.imageKey);
      });
      return s;
    }
    // Everything on screen in other tiles: their current pictures, plus any
    // picture still fading out of a tile.
    function onScreen(exceptCell) {
      var s = keysIn('active', exceptCell);
      cells().forEach(function (c) {
        if (c === exceptCell || !c._cfFading) return;
        var im = slot(c, 'inactive');
        if (im && im.dataset.imageKey) s.add(im.dataset.imageKey);
      });
      return s;
    }
    function pick(hardExclude, softExclude) {
      var pool = el.allImages.filter(function (im) { return !hardExclude.has(im.key); });
      if (!pool.length) return null;
      var better = pool.filter(function (im) { return !softExclude.has(im.key); });
      var from = better.length ? better : pool;
      return from[Math.floor(Math.random() * from.length)];
    }
    function setImg(img, im) {
      img.src = im.src;
      img.alt = im.alt || '';
      img.dataset.imageKey = im.key;
    }

    function swap(cell) {
      var activeImg = slot(cell, 'active');
      var inactiveImg = slot(cell, 'inactive');
      if (!activeImg || !inactiveImg) return;
      var nextKey = inactiveImg.dataset.imageKey;
      // Re-check at the last moment: another tile may have taken it meanwhile.
      if (!nextKey || onScreen(cell).has(nextKey)) return;
      var oldKey = activeImg.dataset.imageKey;

      activeImg.classList.remove('ai-active');
      activeImg.classList.add('ai-inactive');
      inactiveImg.classList.remove('ai-inactive');
      inactiveImg.classList.add('ai-active');
      activeImg.dataset.slot = 'inactive';
      inactiveImg.dataset.slot = 'active';
      if (el.currentlyVisible && el.currentlyVisible.delete) {
        if (oldKey) el.currentlyVisible.delete(oldKey);
        el.currentlyVisible.add(nextKey);
      }

      // Queue this tile's next picture only after the outgoing one has faded
      // out (the fade is 0.6s), so no third picture flashes mid-fade.
      cell._cfBusy = true;
      cell._cfFading = true;
      setTimeout(function () {
        cell._cfFading = false;
        var future = pick(onScreen(null), keysIn('inactive', cell));
        if (future) setImg(activeImg, future); /* activeImg is now this tile's hidden slot */
        cell._cfBusy = false;
      }, 700);
    }

    el.rotateCell = function (cell) {
      if (!cell || cell._cfBusy || !shown(cell)) return;
      var activeImg = slot(cell, 'active');
      var inactiveImg = slot(cell, 'inactive');
      if (!activeImg || !inactiveImg) return;

      var visibleElsewhere = onScreen(cell);
      var nextKey = inactiveImg.dataset.imageKey;
      var conflict = !nextKey || !inactiveImg.getAttribute('src') ||
        nextKey === activeImg.dataset.imageKey || visibleElsewhere.has(nextKey);

      if (conflict) {
        var hard = new Set(visibleElsewhere);
        if (activeImg.dataset.imageKey) hard.add(activeImg.dataset.imageKey);
        var rep = pick(hard, keysIn('inactive', cell));
        if (!rep) return; /* nothing free this cycle — try again next cycle */
        setImg(inactiveImg, rep);
      }

      // Only swap once the incoming picture is decoded (no fade to an empty tile).
      cell._cfBusy = true;
      var ready = inactiveImg.decode ? inactiveImg.decode() : Promise.resolve();
      ready.then(
        function () { cell._cfBusy = false; swap(cell); },
        function () { cell._cfBusy = false; }
      );
    };
  }

  function initGallery() {
    var titles = document.querySelectorAll('[class^="ai-rotating-title-"]');
    for (var i = 0; i < titles.length; i++) fixRotatingTitle(titles[i]);
    var gals = document.querySelectorAll('[class^="ai-gallery-"]');
    for (var j = 0; j < gals.length; j++) {
      if (gals[j].localName.indexOf('gallery-block-') === 0) fixGallery(gals[j]);
    }
  }

  /* ------------------------------------------------------------------
     BOOT
     ------------------------------------------------------------------ */
  function initAll() { initDropdowns(); initGallery(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // The options app builds its dropdowns after load, and collection grids
  // re-render after filtering/sorting — enhance any new dropdowns as they appear.
  if (window.MutationObserver) {
    var rescan = debounceMax(function () { dropStale(document); initDropdowns(); }, 120, 500);
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        if (t.nodeType !== 1) t = t.parentElement;
        // ignore our own dropdowns and the options app's message (rewritten every 50ms)
        if (t && t.closest && t.closest('.cf-sort, .bcpo-validation')) continue;
        rescan();
        return;
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/* =====================================================================
   4) MOBILE MENU — "By Shape" / "By Style" always open  (tickets #20/#21)
   CipherForces, 2026-09-25. Works on the drawer copy that mobile-nav.js
   builds (.redesign-mn-panel); the theme's own hidden drawer is untouched.
   Every submenu that the theme renders as an icon grid (the "By Shape",
   "Shop By Shape" and "By Style" groups — .slide-nav__item--divider with a
   .slide-nav__dropdown--icon-grid) gets its "open this submenu" button
   replaced by a plain heading, so the group is simply part of the parent
   list (Engagement / Diamonds) and there is nothing to tap open or closed.
   The layout (2-column icon + name grid) is in redesign.css and only
   applies to groups marked here, so if this script does not run the menu
   keeps its previous drill-down behaviour.
   ===================================================================== */
(function () {
  'use strict';

  function buildMenuGroups() {
    var panel = document.querySelector('.redesign-mn-panel');
    if (!panel) return false;
    if (panel.getAttribute('data-cf-groups') === '1') return true;
    panel.setAttribute('data-cf-groups', '1');

    var grids = panel.querySelectorAll('.slide-nav__item--divider > .slide-nav__dropdown--icon-grid');
    for (var i = 0; i < grids.length; i++) {
      var grid = grids[i];
      var item = grid.parentElement;
      var btn = null;
      for (var k = 0; k < item.children.length; k++) {
        if (item.children[k].classList.contains('slide-nav__button')) { btn = item.children[k]; break; }
      }
      var labelEl = btn && btn.querySelector('.slide-nav__link > span:not(.visually-hidden)');
      var label = (labelEl ? labelEl.textContent : '').replace(/\s+/g, ' ').trim();
      if (label && !/^shop\b/i.test(label)) label = 'Shop ' + label; // "By Shape" -> "Shop By Shape"

      var title = document.createElement('p');
      title.className = 'redesign-mn-group__title';
      title.id = 'cf-mn-group-' + i;
      title.textContent = label;
      if (btn) item.replaceChild(title, btn); else item.insertBefore(title, grid);

      item.classList.add('redesign-mn-group');
      if (grid.querySelector('.shape-menu-icon--style')) item.classList.add('redesign-mn-group--style');
      grid.classList.add('redesign-mn-group__grid');
      grid.setAttribute('aria-labelledby', title.id);
    }
    return true;
  }

  // mobile-nav.js (loaded just before this file) builds the panel as soon as it runs;
  // fall back to later hooks in case the load order ever changes.
  if (!buildMenuGroups()) {
    document.addEventListener('DOMContentLoaded', buildMenuGroups);
    window.addEventListener('load', buildMenuGroups);
  }
})();

/* =====================================================================
   5) DIAMOND APP (Nivoda Connect) — US spelling "Color" (ticket A6)
   Pages with #diamond-proxy-root: /pages/diamonds (Natural, Lab Grown,
   Colored/Fancy), /pages/gemstones, /pages/build-your-diamond-ring.
   The app has no wording setting, and part of its text comes from
   Nivoda's data ("Colour: J", "Colour L, Clarity SI2", "Near Colourless"),
   so the spelling is corrected where it is shown. Only the text of
   existing text nodes (and alt / title / aria-label) is changed — no
   element is added, removed or moved — so React keeps full control of
   the DOM; when React re-renders a label, the observer corrects it again
   before the next paint. Does nothing on pages without the app.
   Revert = delete this block.
   ===================================================================== */
(function () {
  'use strict';
  var root = document.getElementById('diamond-proxy-root');
  if (!root || !window.MutationObserver) return;

  var HAS = /colour/i;
  var ALL = /colour/gi;
  var ATTRS = ['alt', 'title', 'aria-label'];
  var PORTAL = '[data-radix-popper-content-wrapper], [data-radix-portal]';

  // Colour -> Color, COLOUR -> COLOR, Colourless -> Colorless (drops the "u", keeps the case)
  function us(m) { return m.slice(0, 4) + m.charAt(5); }

  // Only the app's own UI: its root, and the dropdown layers it portals into <body>
  function inApp(node) {
    var el = node.nodeType === 1 ? node : node.parentElement;
    if (!el) return false;
    return root.contains(el) || !!el.closest(PORTAL) ||
      (el.parentElement === document.body && !!el.querySelector(PORTAL));
  }

  function fixText(t) {
    var v = t.nodeValue, p = t.parentNode;
    if (!v || !HAS.test(v) || (p && /^(SCRIPT|STYLE|TEXTAREA)$/.test(p.nodeName))) return;
    t.nodeValue = v.replace(ALL, us);
  }
  function fixAttrs(el) {
    for (var i = 0; i < ATTRS.length; i++) {
      var v = el.getAttribute(ATTRS[i]);
      if (v && HAS.test(v)) el.setAttribute(ATTRS[i], v.replace(ALL, us));
    }
  }
  function fixTree(node) {
    if (node.nodeType === 3) { fixText(node); return; }
    if (node.nodeType !== 1) return;
    if (HAS.test(node.textContent)) {
      var w = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null), t;
      while ((t = w.nextNode())) fixText(t);
    }
    fixAttrs(node);
    var tagged = node.querySelectorAll('[alt], [title], [aria-label]');
    for (var i = 0; i < tagged.length; i++) fixAttrs(tagged[i]);
  }

  fixTree(root);
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var r = records[i];
      if (r.type === 'childList') {
        for (var j = 0; j < r.addedNodes.length; j++) {
          if (inApp(r.addedNodes[j])) fixTree(r.addedNodes[j]);
        }
      } else if (inApp(r.target)) {
        if (r.type === 'characterData') fixText(r.target);
        else fixAttrs(r.target);
      }
    }
  }).observe(document.body, {
    childList: true, subtree: true, characterData: true,
    attributes: true, attributeFilter: ATTRS
  });
})();

/* 6) (2026-09-29, T-1003) removed 2026-10-12: replaced by block 9 below. */

/* =====================================================================
   7. DESKTOP MENU: ONLY ONE DROPDOWN OPEN  (CipherForces, 2026-10-12,
   ticket T-1012). The theme's hover menu (assets/header.desktop-nav.
   nav-dropdown.js) opens a <details> on mouseover and closes it only on
   mouseleave. When the browser misses that mouseleave (Safari, fast moves,
   the page scrolling under the pointer), the old dropdown stays open and
   the next one opens on top, so two menus show at once. Here: whenever a
   menu opens, every other open menu is closed, and moving the pointer
   off the whole header closes them all. The theme file is not changed.
   ===================================================================== */
(function () {
  "use strict";
  var SEL = ".site-nav__details";
  function closeOthers(keep) {
    var all = document.querySelectorAll(SEL + "[open]");
    for (var i = 0; i < all.length; i++) {
      if (all[i] === keep) continue;
      all[i].removeAttribute("open");
      all[i].setAttribute("aria-expanded", "false");
      var s = all[i].querySelector("summary");
      if (s) s.setAttribute("aria-expanded", "false");
    }
  }
  // any way a menu opens (hover, click, keyboard) fires "toggle" on it
  document.addEventListener("toggle", function (e) {
    var d = e.target;
    if (d && d.matches && d.matches(SEL) && d.open) closeOthers(d);
  }, true);
  // pointer over a top-level item: it is the only one allowed open
  document.addEventListener("mouseover", function (e) {
    var d = e.target && e.target.closest ? e.target.closest(SEL) : null;
    if (d) closeOthers(d);
  }, true);
  // pointer leaves the header (missed mouseleave on the item): close all
  function bindHeader() {
    var h = document.querySelector("#SiteHeader") || document.querySelector(".site-header");
    if (!h || h.dataset.cfMenuClose) return;
    h.dataset.cfMenuClose = "1";
    h.addEventListener("mouseleave", function () {
      if (!document.querySelector(".site-nav__details[data-hover='true'][open]")) return;
      closeOthers(null);
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", bindHeader); else bindHeader();
})();

/* =====================================================================
   8. PRODUCT PHOTOS THAT FAIL TO LOAD ARE RETRIED  (CipherForces,
   2026-10-12, ticket T-1007). On a slow or unstable connection, a page
   that asks for dozens of photos at once can lose a few requests; Safari
   then shows a blank box with a "?" and never tries again (the client saw
   this almost daily, more on the office connection). The files themselves
   are fine. Each failed store photo is now retried:
     1st retry after 0.8 s (same address, fresh request),
     2nd retry after 2.5 s from Shopify's other address (cdn.shopify.com)
       as a plain JPEG,
     3rd retry after 6 s, plain JPEG, single size.
   Photos that failed before this script ran are found and retried too.
   Only images on this store's CDN are touched.
   ===================================================================== */
(function () {
  "use strict";
  var MAX = 3, DELAYS = [800, 2500, 6000];
  var LOCAL = /\/\/(?:www\.)?18karats\.com\/cdn\/shop\//;
  var CDN = "//cdn.shopify.com/s/files/1/0708/5067/4918/";
  function isStore(u) { return !!u && (LOCAL.test(u) || u.indexOf("cdn.shopify.com/s/files/1/0708/5067/4918/") !== -1); }
  function addParam(u, k, v) {
    u = u.replace(new RegExp("([?&])" + k + "=[^&\s]*&?"), "$1").replace(/[?&]$/, "");
    return u + (u.indexOf("?") === -1 ? "?" : "&") + k + "=" + v;
  }
  function toCdn(u) { return u.replace(LOCAL, CDN); }
  function mapSrcset(ss, fn) {
    return ss.split(",").map(function (part) {
      var p = part.trim().split(/\s+/); if (!p[0]) return part;
      p[0] = fn(p[0]); return p.join(" ");
    }).join(", ");
  }
  function retry(img) {
    var n = +(img.dataset.cfRetry || 0);
    var cur = img.currentSrc || img.src;
    if (n >= MAX || !isStore(cur)) return;
    img.dataset.cfRetry = n + 1;
    setTimeout(function () {
      var ss = img.getAttribute("srcset") || "";
      var src = img.getAttribute("src") || cur;
      var fix;
      if (n === 0) fix = function (u) { return addParam(u, "cfr", "1"); };
      else fix = function (u) { return addParam(addParam(toCdn(u), "format", "pjpg"), "cfr", String(n + 1)); };
      if (n >= 2) {               // last try: one plain JPEG, no srcset
        img.removeAttribute("srcset");
        img.removeAttribute("sizes");
        var one = img.currentSrc || src;
        img.src = addParam(fix(one), "width", "720");
        return;
      }
      if (ss) img.setAttribute("srcset", mapSrcset(ss, fix));
      img.src = fix(src);
    }, DELAYS[n]);
  }
  // failed requests: "error" does not bubble, so listen in the capture phase
  document.addEventListener("error", function (e) {
    var t = e.target;
    if (t && t.tagName === "IMG") retry(t);
  }, true);
  // photos that already failed before this (deferred) script ran
  function sweep() {
    var imgs = document.images;
    for (var i = 0; i < imgs.length; i++) {
      var im = imgs[i];
      if (im.complete && im.naturalWidth === 0 && (im.currentSrc || im.src) && !im.dataset.cfRetry) retry(im);
    }
  }
  if (document.readyState === "complete") sweep(); else window.addEventListener("load", sweep);
  setTimeout(sweep, 1500);
})();

/* =====================================================================
   9. PRODUCT PAGE DROPDOWNS: ONE SMOOTH MOTION  (CipherForces, 2026-10-12,
   ticket T-1003 reopened). Description / Product Details used a timer-based
   height animation on the inner panel (snippets/block.product-description
   .liquid): a small jump when opening starts and when closing ends (the
   timer fired before the animation finished), taps ignored while moving,
   and on phones the text was clipped and re-faded mid-way. The client saw
   it as jitter on phone and desktop. Now the whole dropdown animates its
   own height between the closed and open size (Web Animations), a second
   tap mid-way reverses from where it is, the gold title and arrow change at
   the tap, and reduced-motion users get an instant toggle. The snippet's
   own handler is bypassed (event stopped in the capture phase).
   ===================================================================== */
(function () {
  "use strict";
  var DUR = 320, EASE = "cubic-bezier(.25,.8,.25,1)";
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  function closedHeight(det, head) {
    var cs = getComputedStyle(det);
    return head.getBoundingClientRect().height + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth) + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
  }
  function finish(det, closing) {
    det._cfAnim = null;
    if (closing) det.open = false;
    det.style.height = ""; det.style.overflow = "";
    det.classList.remove("cf-acc-closing", "cf-acc-moving");
  }
  function toggle(det, head) {
    var body = det.querySelector(".pdp-acc__body");
    var goingOpen = !det.open || det.classList.contains("cf-acc-closing");
    if (reduce || !det.animate) {
      if (goingOpen) det.open = true; else det.open = false;
      det.classList.remove("cf-acc-closing");
      return;
    }
    var from = det.getBoundingClientRect().height;      // where it is now, even mid-way
    if (det._cfAnim) { det._cfAnim.onfinish = null; det._cfAnim.cancel(); }
    det.style.overflow = "hidden";
    det.classList.add("cf-acc-moving");
    var to;
    if (goingOpen) {
      det.classList.remove("cf-acc-closing");
      det.open = true;
      det.style.height = "";
      to = det.getBoundingClientRect().height;          // full open height
    } else {
      det.classList.add("cf-acc-closing");              // title and arrow turn back now
      to = closedHeight(det, head);
    }
    det.style.height = from + "px";
    var a = det.animate([{ height: from + "px" }, { height: to + "px" }], { duration: DUR, easing: EASE });
    if (body && body.animate) body.animate([{ opacity: goingOpen ? 0.25 : 1 }, { opacity: goingOpen ? 1 : 0.25 }], { duration: DUR, easing: EASE });
    det.style.height = to + "px";
    det._cfAnim = a;
    a.onfinish = function () { finish(det, !goingOpen); };
  }
  document.addEventListener("click", function (e) {
    var head = e.target && e.target.closest ? e.target.closest(".pdp-acc-group .pdp-acc__head") : null;
    if (!head) return;
    var det = head.parentElement;
    if (!det || !det.classList.contains("pdp-acc")) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    toggle(det, head);
  }, true);
})();
